import axios from "axios";
import { get, isEmpty, trim } from "lodash";
import { toastr } from "react-redux-toastr";
import { Group } from "../../utils/logger";

import {
  ADMIN_ERROR,
  ADMIN_LOADING,
  STORE_USER,
  SET_USERS,
  SELECT_USER,
  SET_SEEDS,
  SELECT_SEED,
  SET_WHITELIST,
  SELECT_WHITELIST,
  SET_MBID_LOOKUP,
  SET_PREFERENCE_LOOKUP,
  SET_UNIQUEID_LOOKUP,
  SET_SPAMCHECK,
} from "../constants";

const LoggerGroup = new Group("Admin Actions", { disable: true });

export const adminLoading = (type) => (dispatch) => {
  dispatch({ type: ADMIN_LOADING, payload: type });
};

// ================================================================================================
// User Operations ================================================================================
// ================================================================================================

export const selectUser = (approver) => (dispatch) => {
  dispatch({ type: SELECT_USER, payload: approver });
};

export const storeAdminUser = (payload) => (dispatch) => {
  dispatch({ type: STORE_USER, payload });
};

let usersCache;
export const getUsers = (force_refresh, include = "active") => (dispatch, getState) => {
  const { users, token } = getState().admin;

  if (isEmpty(users) || force_refresh) {
    dispatch(adminLoading("users"));

    LoggerGroup.emit("[i] Updating list of users");

    usersCache = axios({
      url: `admin/users`,
      method: "get",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: { include },
    })
      .then((res) => {
        const users = get(res, "data.users");
        if (users) {
          LoggerGroup.emit("  [i] List of users", { table: users }).end();
          dispatch({ type: SET_USERS, payload: users });
        }
      })
      .catch((err) => {
        LoggerGroup.emit({ warn: "[i] Error getting users" }, { dir: err.response }).end();
        dispatch({ type: ADMIN_ERROR, payload: { users: "Error getting users" } });
      });
  }

  return usersCache;
};

let mbidLookUpCache;

export const getMbIdLookUpResult = (mbIdFilter) => (dispatch, getState) => {
  LoggerGroup.emit(" [i] MbId LookUp", mbIdFilter);
  dispatch(adminLoading("mbIdLookUpResult"));

  mbidLookUpCache = axios
    .get(`admin/mbid-lookup/${mbIdFilter.mbId}/${mbIdFilter.dt_create}`)
    .then((res) => {
      const mbidLookUpResult = get(res, "data.mbIdLookUpResult");
      if (mbidLookUpResult) {
        LoggerGroup.emit("  [i] MbId Look Up", { table: mbidLookUpResult }).end();
        dispatch({ type: SET_MBID_LOOKUP, payload: mbidLookUpResult });
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");
      toastr.error("MbId Lookup Error", error_message);
      LoggerGroup.emit({ warn: "[i] Error getting mbIdLookUp" }, { dir: error_message }).end();
      dispatch({
        type: ADMIN_ERROR,
        payload: { mbidLookUp: `Error getting MbId Lookup for code ${mbIdFilter.mbId}` },
      });
      dispatch({
        type: SET_MBID_LOOKUP,
        payload: {
          mbidLookUpResult: {
            email: [],
            sms: [],
            voice: [],
            messages: [],
            preference: [],
            disposition: [],
          },
        },
      });
    });

  return mbidLookUpCache;
};

let uniqueidLookUpCache;
export const getUniqueIdLookUpResult = (uniqueId) => (dispatch, getState) => {
  LoggerGroup.emit(" [i] UniqueId LookUp", uniqueId);
  dispatch(adminLoading("uniqueIdLookUpResult"));

  if (!uniqueId || trim(uniqueId) === "") {
    dispatch({ type: SET_UNIQUEID_LOOKUP, payload: [] });
    return;
  }

  uniqueidLookUpCache = axios
    .get(`admin/uniqueid-lookup/${uniqueId}`)
    .then((res) => {
      const uniqueIdLookUpResult = get(res, "data.uniqueIdLookUpResult");
      if (uniqueIdLookUpResult) {
        LoggerGroup.emit("  [i] UniqueId Look Up", { table: uniqueIdLookUpResult }).end();
        dispatch({ type: SET_UNIQUEID_LOOKUP, payload: uniqueIdLookUpResult });
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");
      LoggerGroup.emit(
        { error: "[i] Error getting uniqueIdLookUpResult" },
        { dir: error_message }
      ).end();
      toastr.error("UniqueId Lookup Error", error_message);
      dispatch({
        type: ADMIN_ERROR,
        payload: { uniqueIdLookUpResult: `Error getting UniqueId Lookup ${uniqueId}` },
      });
      dispatch({ type: SET_UNIQUEID_LOOKUP, payload: { uniqueIdLookUpResult: [] } });
    });

  return uniqueidLookUpCache;
};

let preferenceLookUpCache;

export const getPreferenceLookUp = (accountNumber) => (dispatch, getState) => {
  LoggerGroup.emit(" [i] Preference LookUp", accountNumber);
  dispatch(adminLoading("preferenceLookUpResult"));

  preferenceLookUpCache = axios
    .get(`admin/preference-lookup/${accountNumber}`)
    .then((res) => {
      const preferenceLookUpResult = get(res, "data.preferenceLookUpResult");
      LoggerGroup.emit("  [i] Preference Look Up", { table: preferenceLookUpResult }).end();
      if (preferenceLookUpResult)
        dispatch({ type: SET_PREFERENCE_LOOKUP, payload: preferenceLookUpResult });
      else dispatch({ type: SET_PREFERENCE_LOOKUP, payload: { preferenceLookUpResult: [] } });
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");
      LoggerGroup.emit(
        { error: `[i] Error getting Account Preference ${accountNumber}` },
        { dir: error_message }
      ).end();
      toastr.error(`Preference Lookup Error For Account Number ${accountNumber}`, error_message);
      dispatch({
        type: ADMIN_ERROR,
        payload: {
          preferenceLookUpResult: `Error getting Preference Details for account  ${accountNumber}`,
        },
      });
      dispatch({ type: SET_PREFERENCE_LOOKUP, payload: { preferenceLookUpResult: [] } });
    });

  return preferenceLookUpCache;
};

export const updateUserPermissions = (user) => (dispatch) => {
  LoggerGroup.emit(`[i] Updating user[${user.user_id_bint}] permissions`);

  return axios
    .patch(`admin/permissions/${user.user_id_bint}`, user)
    .then((res) => {
      const updatedUser = get(res, "data.user");
      if (updatedUser) {
        LoggerGroup.emit("  [i] Updated user", { table: updatedUser }).end();

        dispatch(getUsers(true));
        dispatch({ type: SELECT_USER, payload: user });
        toastr.success("Success", `Updated permissions for "${updatedUser.firstname_vch}"`);

        return updatedUser;
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({ warn: `[i] Error updating user[${user.user_id_bint}] permissions` }).end();

      dispatch({ type: ADMIN_ERROR, payload: { updatePermission: "Error updating user" } });
    });
};

// ================================================================================================
// Seed List Operations ===========================================================================
// ================================================================================================

export const selectSeedList = (approver) => (dispatch) => {
  dispatch({ type: SELECT_SEED, payload: approver });
};

let seedListsCache;
export const getSeedLists = (force_refresh) => (dispatch, getState) => {
  const { admin } = getState();
  if (isEmpty(admin.seeds) || force_refresh) {
    dispatch(adminLoading("seedLists"));

    LoggerGroup.emit("[i] Updating list of seeds");

    seedListsCache = axios
      .get("admin/seed-lists")
      .then((res) => {
        const seedLists = get(res, "data.seedLists");
        if (seedLists) {
          LoggerGroup.emit("  [i] List of seeds", { table: seedLists }).end();
          dispatch({ type: SET_SEEDS, payload: seedLists });
        }
      })
      .catch((err) => {
        LoggerGroup.emit({ warn: "[i] Error getting seeds" }, { dir: err.response }).end();
        dispatch({ type: ADMIN_ERROR, payload: { delete: "Error getting seeds" } });
      });
  }

  return seedListsCache;
};

export const createSeedList = (seedList) => (dispatch) => {
  LoggerGroup.emit("[i] Creating Seed List");

  return axios
    .put("admin/seed-list", seedList)
    .then((res) => {
      const newSeedList = get(res, "data.seedList");
      if (newSeedList) {
        LoggerGroup.emit("  [i] Created Seed List", { table: newSeedList }).end();

        dispatch(getSeedLists(true));
        dispatch({ type: SELECT_SEED, payload: newSeedList });
        toastr.success("Success", `Created Seed List "${newSeedList.name_vch}"`);

        return newSeedList;
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({ warn: "[i] Error creating Seed List" }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error creating Seed List" } });
    });
};

export const updateSeedList = (seedList) => (dispatch) => {
  LoggerGroup.emit(`[i] Updating Seed List[${seedList.seed_list_id_bint}]`);

  return axios
    .patch(`admin/seed-list/${seedList.seed_list_id_bint}`, seedList)
    .then((res) => {
      const newSeedList = get(res, "data.seedList");
      if (newSeedList) {
        LoggerGroup.emit("  [i] Updated seed", { table: newSeedList }).end();

        dispatch(getSeedLists(true));
        dispatch({ type: SELECT_SEED, payload: newSeedList });
        toastr.success("Success", `Updated seed: ${newSeedList.name_vch}`);

        return newSeedList;
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({
        warn: `[i] Error updating Seed List[${seedList.seed_list_id_bint}]`,
      }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error updating Seed List" } });
    });
};

export const deleteSeedList = (seedList) => (dispatch) => {
  LoggerGroup.emit(`[i] Deleting Seed List[${seedList.seed_list_id_bint}]`);

  return axios
    .delete(`admin/seed-list/${seedList.seed_list_id_bint}`)
    .then((res) => {
      const status = get(res, "data.status");
      if (status === "success") {
        LoggerGroup.emit(`  [i] Deleted Seed List[${seedList.seed_list_id_bint}]`).end();

        dispatch(getSeedLists(true));
        toastr.success("Success", `Deleted Seed List: ${seedList.name_vch}]`);
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({
        warn: `[i] Error deleting Seed List[${seedList.seed_list_id_bint}]`,
      }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error deleting Seed List" } });
    });
};

// ================================================================================================
// White List Operations ===========================================================================
// ================================================================================================

export const selectWhitelist = (whitelist) => (dispatch) => {
  dispatch({ type: SELECT_WHITELIST, payload: whitelist });
};

let whitelistCache;
export const getWhitelist = (force_refresh) => (dispatch, getState) => {
  const { admin } = getState();

  if (isEmpty(admin.whitelist) || force_refresh) {
    dispatch(adminLoading("whitelist"));

    LoggerGroup.emit("[i] Updating whitelist");

    whitelistCache = axios
      .get("admin/whitelist")
      .then((res) => {
        const payload = get(res, "data");
        if (payload.whitelist) {
          LoggerGroup.emit("  [i Whitelist ", { table: payload.whitelist }).end();
          dispatch({ type: SET_WHITELIST, payload });
        }
      })
      .catch((err) => {
        LoggerGroup.emit({ warn: "[i] Error getting whitelist" }, { dir: err.response }).end();
        dispatch({ type: ADMIN_ERROR, payload: { whitelist: "Error getting whitelist" } });
      });
  }

  return whitelistCache;
};

export const createWhitelist = (whitelist) => (dispatch) => {
  LoggerGroup.emit("[i] Creating Whitelist");

  return axios
    .put("admin/whitelist", whitelist)
    .then((res) => {
      const newWhitelist = get(res, "data.whitelist");
      if (newWhitelist) {
        LoggerGroup.emit("  [i] Created Whitelist", { table: newWhitelist }).end();

        dispatch(getWhitelist(true));
        dispatch({ type: SELECT_WHITELIST, payload: newWhitelist });
        toastr.success("Success", `Created Whitelist record "${newWhitelist.description_vch}"`);

        return newWhitelist;
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({ warn: "[i] Error adding Whitelist record" }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error adding Whitelist record" } });
    });
};

export const updateWhitelist = (whitelist) => (dispatch) => {
  LoggerGroup.emit(
    `[i] Updating Whitelist contact[${whitelist.contact_vch}] on channel[${whitelist.channel_vch}]`
  );

  return axios
    .patch(`admin/whitelist`, whitelist)
    .then((res) => {
      const newWhitelist = get(res, "data.whitelist");
      if (newWhitelist) {
        LoggerGroup.emit("  [i] Updated whitelist record", { table: newWhitelist }).end();

        dispatch(getWhitelist(true));
        dispatch({ type: SELECT_WHITELIST, payload: newWhitelist });
        toastr.success(
          "Success",
          `Updated whitelist: contact[${whitelist.contact_vch}] on channel[${whitelist.channel_vch}]`
        );

        return newWhitelist;
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({
        warn: `[i] Error updating Whitelist contact[${whitelist.contact_vch}] on channel[${whitelist.channel_vch}]`,
      }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error updating Whitelist" } });
    });
};

export const deleteWhitelist = ({ channel_vch, contact_vch }) => (dispatch) => {
  LoggerGroup.emit(`[i] Deleting Whitelist contact[${contact_vch}] on channel[${channel_vch}]`);

  return axios
    .delete(`admin/whitelist/${channel_vch}/${contact_vch}`)
    .then((res) => {
      const status = get(res, "data.status");
      if (status === "success") {
        LoggerGroup.emit(
          `  [i] Deleted record from Whitelist contact[${contact_vch}] on channel[${channel_vch}]`
        ).end();

        dispatch(getWhitelist(true));
        toastr.success(
          "Success",
          `Deleted record from Whitelist contact[${contact_vch}] on channel[${channel_vch}]`
        );
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({
        warn: `[i] Error deleting Whitelist contact[${contact_vch}] on channel[${channel_vch}]`,
      }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error deleting record from Whitelist" } });
    });
};

export const getSpamCheck = (emailaddress) => (dispatch, getState) => {
  LoggerGroup.emit(" [i] Spam check of Email Address ", emailaddress);
  dispatch(adminLoading("spamCheckResult"));
  let spamCheckCache = axios
    .get(`admin/spamreport/${emailaddress}`)
    .then((res) => {
      const spamCheckResult = get(res, "data");
      if (spamCheckResult) {
        LoggerGroup.emit("  [i] Spam Email Search ", { table: spamCheckResult }).end();
        dispatch({ type: SET_SPAMCHECK, payload: spamCheckResult });
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");
      LoggerGroup.emit(
        { warn: "[i] Error verifying email address spam check" },
        { dir: error_message }
      ).end();
      dispatch({
        type: ADMIN_ERROR,
        payload: { spamCheck: `Error searching Spam Email address ${emailaddress}` },
      });
      dispatch({
        type: SET_SPAMCHECK,
        payload: getState().admin.spamCheckResult || [],
      });
    });

  return spamCheckCache;
};

export const deleteSpamCheck = (emailaddress) => (dispatch, getState) => {
  LoggerGroup.emit(" [i] Spam check of Email Address ", emailaddress);
  dispatch(adminLoading("spamCheckResult"));
  let spamCheckCache = axios
    .delete(`admin/spamreport/${emailaddress}`)
    .then((res) => {
      const spamCheckResult = get(res, "data");
      LoggerGroup.emit("  [i] Spam Email Search ", { table: spamCheckResult }).end();
      dispatch({ type: SET_SPAMCHECK, payload: [] });
      toastr.success("Success", `Email ${emailaddress} successfully removed from spam check `);
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");
      toastr.error("Spam Email Deletion Error", error_message);
      LoggerGroup.emit(
        { warn: "[i] Error verifying email address spam check" },
        { dir: error_message }
      ).end();
      dispatch({
        type: ADMIN_ERROR,
        payload: { spamCheck: `Error deleting Spam Email address ${emailaddress}` },
      });
      dispatch({
        type: SET_SPAMCHECK,
        payload: getState().admin.spamCheckResult || [],
      });
    });

  return spamCheckCache;
};
